import React, { useState, useEffect } from 'react'
import { Trans } from '@lingui/macro'
import classNames from 'classnames'
import ChartJS, {
  IChartJSProps
} from '../../../components/common/charts/ChartJS'

import { lDateFormatsPerGranularity } from '../chartOptions'
import { TimeUnitEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import dayjs, { OpUnitType } from 'dayjs'
import { LinkBox, Flex } from 'front-end/src/components'
import {
  DateFilterType,
  useDateFilterContext
} from 'front-end/src/components/common/dateFilter'

interface IAnalyticsChartProps extends IChartJSProps {
  config: Chart.ChartConfiguration
  data: IChartDataProps[]
  onGranularityChanged: (granularity: TimeUnitEnum) => void
  granularity: TimeUnitEnum
}

export interface IChartDataItem {
  count: number
  day: string
}

export interface IChartDataProps {
  dataset: IChartDataItem[]
  color?: string
  tooltipLabel?: string
}

function buildChartJSData(
  data: IChartDataProps[],
  granularity: TimeUnitEnum,
  filter: DateFilterType
) {
  const { from, to } = filter
  const ldateFmt = lDateFormatsPerGranularity[granularity]
  const dateFmt = 'D MMM'
  const defaultColor = '0,187,119'
  const graphData = []
  const labels: string[] = []

  let datasetLength = 0
  data.forEach((row) => {
    datasetLength += row.dataset.length
  })

  data.forEach((row, i) => {
    const color = row.color || defaultColor

    const rowData = {
      data: [],
      label: row.tooltipLabel,
      borderWidth: 2,
      stepped: true,
      pointRadius: 0,
      lineTension: 0.3,
      fill: i === 0,
      borderColor: `rgba(${color},1)`,
      pointBackgroundColor: `rgba(${color},1)`,
      backgroundColor: `transparent`
    }

    let date = dayjs(from)
    while (date.isBefore(dayjs(to).add(1, 'day'))) {
      const ldate = dayjs(date)
        .startOf(granularity.toLowerCase() as OpUnitType)
        .format(ldateFmt)
      const label = dayjs(date)
        .startOf(granularity.toLowerCase() as OpUnitType)
        .format(dateFmt)

      if (!labels.includes(label)) {
        labels.push(label)
      }

      let countForDate = 0
      const found = row.dataset.find((dataItem) => {
        const x = dayjs(dataItem.day)
          .startOf(granularity.toLowerCase() as OpUnitType)
          .format(ldateFmt)
        if (ldate === x) {
          countForDate += dataItem.count
          return true
        }
        return false
      })
      // @ts-expect-error 9/11/2020
      rowData.data.push(found ? countForDate : 0)

      date = dayjs(date).add(1, granularity.toLowerCase() as OpUnitType)
    }

    // @ts-expect-error 9/11/2020
    graphData.push(rowData)
  })

  return {
    labels,
    datasets: graphData,
    noData: !datasetLength
  }
}

const AnalyticsChart = ({
  data,
  config,
  onGranularityChanged,
  granularity,
  ...props
}: IAnalyticsChartProps) => {
  const [dateFilter] = useDateFilterContext()
  const { DAY: day, WEEK: week } = TimeUnitEnum

  const [chartConfig, setChartConfig] = useState(config)

  const makeChangeGranularityFn = (newGranularity: TimeUnitEnum) => () => {
    onGranularityChanged && onGranularityChanged(newGranularity)
  }

  useEffect(() => {
    setChartConfig({
      ...chartConfig,
      data: buildChartJSData(data, granularity, dateFilter)
    })
  }, [granularity])

  return (
    <div className="ActivityChart__wrapper">
      <div className="activity-graph">
        {chartConfig.data && <ChartJS {...props} config={chartConfig} />}
        <div className="activity-graph-settings">
          <Flex className="input-wrapper btn-group">
            <LinkBox
              mr="8px"
              role="button"
              aria-pressed={granularity === day ? 'true' : 'false'}
              className={classNames('button', {
                active: granularity === day
              })}
              onClick={makeChangeGranularityFn(day)}
            >
              <Trans>Day</Trans>
            </LinkBox>
            <LinkBox
              role="button"
              aria-pressed={granularity === week ? 'true' : 'false'}
              className={classNames('button', {
                active: granularity === week
              })}
              onClick={makeChangeGranularityFn(week)}
            >
              <Trans>Week</Trans>
            </LinkBox>
          </Flex>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsChart
